/**
 * Component: AILoadingModal
 * A simple blocking modal with a spinner, message, and optional cancel action.
 * Shows with a brief delay to avoid flashing stale content between opens.
 *
 * Props:
 * - isOpen: boolean
 * - onCancel?: () => void
 * - message?: string
 * - subtitle?: string
 * - showCancel?: boolean
 * - cancelText?: string (unused visually; kept for API compatibility)
 */
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

export function AILoadingModal({ 
  isOpen, 
  onCancel,
  message = 'Processing...',
  subtitle = 'This may take a few moments',
  showCancel = true,
  cancelText = 'Cancel'
}) {
  const [showModal, setShowModal] = useState(false);
  const [currentMessage, setCurrentMessage] = useState(message);
  const [currentSubtitle, setCurrentSubtitle] = useState(subtitle);

  // Handle opening/closing with a slight delay to prevent flash of old content
  useEffect(() => {
    if (isOpen) {
      // Update message immediately when opening
      setCurrentMessage(message);
      setCurrentSubtitle(subtitle);
      // Small delay to ensure content is updated before showing
      const timer = setTimeout(() => {
        setShowModal(true);
      }, 50);
      return () => clearTimeout(timer);
    } else {
      setShowModal(false);
      // Clear content after closing to prevent flash on next open
      const timer = setTimeout(() => {
        setCurrentMessage('Processing...');
        setCurrentSubtitle('This may take a few moments');
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen, message, subtitle]);

  // Update current message/subtitle when they change while modal is open
  useEffect(() => {
    if (showModal) {
      setCurrentMessage(message);
      setCurrentSubtitle(subtitle);
    }
  }, [message, subtitle, showModal]);

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-0">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={showCancel ? onCancel : undefined}
      />

      {/* Modal Content */}
      <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-900 shadow-xl transition-all px-8 py-6 max-w-md w-full mx-4">
        {/* Close button - only show if cancellation is allowed */}
        {showCancel && onCancel && (
          <button
            onClick={onCancel}
            className="absolute right-4 top-4 rounded-lg p-2 text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400 transition-colors"
            aria-label="Cancel"
          >
            <X className="h-5 w-5" />
          </button>
        )}

        <div className="flex flex-col items-center justify-center space-y-6 text-center">
          {/* Loading spinner - matches BasicLoadingIndicator design */}
          <div className="flex justify-center items-center">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-100 dark:border-gray-800 border-b-purple-600 dark:border-b-purple-400"></div>
          </div>
          
          {/* Message */}
          <div className="space-y-2">
            <p className="text-lg font-medium text-gray-900 dark:text-white">
              {currentMessage}
            </p>
            {currentSubtitle && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {currentSubtitle}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 